import type { ComparePageCopy, ComparePageCopyByLang } from "./compare";

const VENDOR = { name: "Fireflies.ai", url: "https://fireflies.ai" };

const en: ComparePageCopy = {
  seo: {
    title: "Fireflies.ai vs. Minuta — notes after vs. answers during",
    description:
      "Honest comparison between Fireflies and Minuta. Pick Fireflies for team notetaking after calls; pick Minuta when you need live answers during hard English, Portuguese, or Spanish calls.",
    keywords:
      "fireflies alternative, fireflies vs minuta, no-bot meeting transcription, recorderbot alternative, fireflies portuguese, secure meeting copilot",
  },
  vendor: VENDOR,
  eyebrow: "Comparison",
  h1: "Fireflies.ai records the call. Minuta helps you answer in it.",
  intro:
    "Fireflies is a strong team notetaker for recordings, transcripts, summaries, and CRM workflows after the meeting. Minuta is for the moment inside a difficult customer or discovery call when you need the next answer now.",
  honesty: {
    heading: "Pick the right one",
    pickThem: {
      name: "Fireflies.ai",
      bullets: [
        "You want recordings, transcripts, and summaries available for the team after the call.",
        "You want the deepest CRM stack — HubSpot, Salesforce, Affinity, Wealthbox, Greenhouse all preconfigured.",
        "A bot in the call is fine for your prospects and clients, and a visible notetaker helps your workflow.",
        "You're enterprise-led and SOC 2 + HIPAA-BAA on Enterprise covers your buyer.",
        "Your team works in 5+ video platforms and you want one notetaker for all of them.",
      ],
    },
    pickMinuta: [
      "You are in a hard sales, customer, or discovery call in English, Portuguese, or Spanish and need help answering while the call is still happening.",
      "Your customer's policy blocks third-party bots — sales into legal, healthcare, finance, public sector.",
      "You want local audio capture plus zero-retention cloud AI processing, not a meeting bot that records the call as another participant.",
      "You speak Portuguese or Spanish and want the website, not just the transcription engine, in your language.",
      "You want a real-time answer copilot, not just a post-meeting summary.",
    ],
  },
  table: {
    heading: "Side by side",
    headers: { feature: "Feature", them: "Fireflies", minuta: "Minuta" },
    rows: [
      {
        feature: "Bot in the meeting",
        them: {
          value: 'Yes — RecorderBot ("Fred") joins as participant',
          cite: { url: "https://fireflies.ai/pricing", capturedAt: "2026-04-30" },
        },
        minuta: { value: "No — no bot, no participant" },
      },
      {
        feature: "Audio processing",
        them: {
          value: "Cloud (US/EU regions)",
          cite: { url: "https://fireflies.ai/security", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Local capture, secure transient API" },
      },
      {
        feature: "Native PT website",
        them: {
          value: "No — English-only website (transcribes 60+ langs)",
          cite: {
            url: "https://fireflies.ai/blog/fireflies-portuguese-transcription",
            capturedAt: "2026-04-30",
          },
        },
        minuta: { value: "Yes — full PT-BR site" },
      },
      {
        feature: "Native ES website",
        them: { value: "No — English-only website" },
        minuta: { value: "Yes — full ES site" },
      },
      {
        feature: "Live answer copilot",
        them: { value: "No — focused on post-meeting summaries" },
        minuta: { value: "Yes (Pro)" },
      },
      {
        feature: "Works offline", them: { value: "No" }, minuta: { value: "No (requires internet)" },
      },
      {
        feature: "CRM integrations",
        them: {
          value: "~42 — HubSpot, Salesforce, Affinity, Wealthbox, Greenhouse",
          cite: { url: "https://fireflies.ai/integrations", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Stripe + Cognito (CRM via export)" },
      },
      {
        feature: "Compliance",
        them: {
          value: "SOC 2 II, GDPR, HIPAA-BAA (Enterprise)",
          cite: { url: "https://fireflies.ai/security", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Privacy-first by design (DPA on roadmap)" },
      },
      {
        feature: "Platform",
        them: {
          value: "Mac, Windows, mobile, Chrome",
          cite: { url: "https://fireflies.ai/pricing", capturedAt: "2026-04-30" },
        },
        minuta: { value: "macOS (Windows soon)" },
      },
      {
        feature: "Free tier",
        them: {
          value: "Free $0 — 800 min/mo",
          cite: { url: "https://fireflies.ai/pricing", capturedAt: "2026-04-30" },
        },
        minuta: { value: "7-day full trial · then 1h/month Free" },
      },
      {
        feature: "Paid (per user, annual)",
        them: {
          value: "From $10/u/mo Pro",
          cite: { url: "https://fireflies.ai/pricing", capturedAt: "2026-04-30" },
        },
        minuta: { value: "€9/mo" },
      },
    ],
    footnote: "Pricing as of 2026-04-30; check each vendor for the current numbers.",
  },
  whenMinuta: {
    heading: "Where Minuta wins",
    bullets: [
      "The live moment matters more than the archive. Fireflies is built for recordings, summaries, and follow-up workflows after the meeting. Minuta is built for the answer you need before the call moves on.",
      "Hard live calls are the problem. Minuta captures audio locally and drafts an answer in your tone while the customer is still waiting.",
      "Bots can be the wrong shape. Fireflies is transparent about Fred joining as a participant; that works for many teams. In regulated or sensitive sales calls, Minuta stays off the participant list.",
      'Privacy posture is different. Fireflies\'s Enterprise "private storage" is still a notetaker cloud. Minuta avoids the meeting bot and uses reviewed zero-retention cloud AI paths for transcription and answers.',
      "Brazilian and LatAm buyers. Fireflies can transcribe PT and ES via its 60+ language list, but ships an English-only website. We rank natively in PT-BR and ES.",
    ],
  },
  also: { heading: "Comparing other tools?", links: [] },
  cta: {
    heading: "Try Minuta on your next customer or discovery call",
    body: "7 days with everything unlocked. Use it when the conversation gets hard and you need the next answer live.",
    primary: "Start free trial",
    secondary: "Download for macOS",
  },
};

const pt: ComparePageCopy = {
  seo: {
    title: "Fireflies.ai vs. Minuta — preço no Brasil e resposta ao vivo",
    description:
      "Comparação honesta entre Fireflies e Minuta para brasileiros: preço em BRL, bot na call, CRM, suporte PT-BR e resposta ao vivo em inglês, português e espanhol.",
    keywords:
      "alternativa fireflies, fireflies vs minuta, preço fireflies no brasil, transcrição de reunião sem bot, fireflies português, copiloto de reunião sem bot",
  },
  vendor: VENDOR,
  eyebrow: "Comparação",
  h1: "Fireflies.ai grava a call. Minuta ajuda você a responder nela.",
  intro:
    "Fireflies é um notetaker forte para gravações, transcrições, resumos e fluxos de CRM depois da reunião. Minuta é para o momento dentro de uma call difícil com cliente ou discovery, quando você precisa da próxima resposta agora — sem bot e com cobrança em Real.",
  honesty: {
    heading: "Escolha o certo",
    pickThem: {
      name: "Fireflies.ai",
      bullets: [
        "Você quer gravações, transcrições e resumos disponíveis pro time depois da call.",
        "Você quer integração profunda de CRM — HubSpot, Salesforce, Affinity, Wealthbox, Greenhouse já prontos.",
        "Bot na call não é problema pros seus prospects e clientes, e um notetaker visível ajuda seu fluxo.",
        "Sua compra é enterprise-led e SOC 2 + HIPAA-BAA no Enterprise atende seu comprador.",
        "Você aceita pagar por seat em USD e tratar câmbio, IOF e cartão internacional como parte do custo.",
        "Seu time trabalha em 5+ plataformas de vídeo e você quer um notetaker pra todas.",
      ],
    },
    pickMinuta: [
      "Você está numa call difícil de vendas, customer ou discovery em inglês, português ou espanhol e precisa de ajuda para responder enquanto a call ainda está acontecendo.",
      "O seu cliente bloqueia bot de terceiro — venda pra jurídico, saúde, financeiro, setor público.",
      "Você quer pagar em Real: Founding 100 por R$22.50/mês nas primeiras 100 vagas, travado por 12 meses.",
      "Você fala português ou espanhol e quer o site na sua língua, não só a transcrição.",
      "Você quer copiloto sugerindo resposta em tempo real, não só resumo depois da call.",
    ],
  },
  table: {
    heading: "Preço e decisão lado a lado",
    headers: { feature: "Recurso", them: "Fireflies", minuta: "Minuta" },
    rows: [
      {
        feature: "Preço no Brasil",
        them: {
          value: "Business: US$19/seat/mês anual (aprox. R$95, antes de IOF)",
          cite: { url: "https://fireflies.ai/pricing?eco_language=english", capturedAt: "2026-05-21" },
        },
        minuta: { value: "Founding 100: R$22.50/mês por 12 meses; depois Pro R$35/mês" },
      },
      {
        feature: "Cobra em Real",
        them: { value: "Não — cobrança em USD" },
        minuta: { value: "Sim — Stripe BR em BRL" },
      },
      {
        feature: "Bot na reunião",
        them: {
          value: 'Sim — RecorderBot ("Fred") entra como participante',
          cite: { url: "https://fireflies.ai/pricing", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Não — sem bot, sem participante" },
      },
      {
        feature: "Processamento do áudio",
        them: {
          value: "Nuvem (regiões EUA/UE)",
          cite: { url: "https://fireflies.ai/security", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Captura local, API temporária segura" },
      },
      {
        feature: "Site nativo em PT",
        them: {
          value: "Não — site só em inglês (transcreve 60+ línguas)",
          cite: {
            url: "https://fireflies.ai/blog/fireflies-portuguese-transcription",
            capturedAt: "2026-04-30",
          },
        },
        minuta: { value: "Sim — site completo em PT-BR" },
      },
      {
        feature: "Site nativo em ES",
        them: { value: "Não — site só em inglês" },
        minuta: { value: "Sim — site completo em ES" },
      },
      {
        feature: "Copiloto de resposta ao vivo",
        them: { value: "Não — foco em resumos pós-reunião" },
        minuta: { value: "Sim (Pro)" },
      },
      {
        feature: "Funciona offline", them: { value: "Não" }, minuta: { value: "Não (precisa de internet)" },
      },
      {
        feature: "Integrações de CRM",
        them: {
          value: "~42 — HubSpot, Salesforce, Affinity, Wealthbox, Greenhouse",
          cite: { url: "https://fireflies.ai/integrations", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Stripe + Cognito (CRM via export)" },
      },
      {
        feature: "Compliance",
        them: {
          value: "SOC 2 II, GDPR, HIPAA-BAA (Enterprise)",
          cite: { url: "https://fireflies.ai/security", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Privacidade por design (DPA no roadmap)" },
      },
      {
        feature: "Plataforma",
        them: {
          value: "Mac, Windows, mobile, Chrome",
          cite: { url: "https://fireflies.ai/pricing", capturedAt: "2026-04-30" },
        },
        minuta: { value: "macOS (Windows em breve)" },
      },
      {
        feature: "Plano grátis",
        them: {
          value: "Free US$ 0 — 800 min/mês",
          cite: { url: "https://fireflies.ai/pricing", capturedAt: "2026-04-30" },
        },
        minuta: { value: "7 dias com tudo · depois 1h/mês no Free" },
      },
      {
        feature: "Pago (por usuário, anual)",
        them: {
          value: "A partir de US$ 10/u/mês Pro",
          cite: { url: "https://fireflies.ai/pricing?eco_language=english", capturedAt: "2026-05-21" },
        },
        minuta: { value: "Pro R$35/mês; Founding 100 R$22.50/mês por 12 meses" },
      },
    ],
    footnote: "Preços dos concorrentes em USD consultados em 2026-05-21; conversão BRL aproximada, antes de IOF e variação cambial.",
  },
  whenMinuta: {
    heading: "Onde o Minuta ganha",
    bullets: [
      "O momento ao vivo importa mais que o arquivo. Fireflies é feito para gravações, resumos e follow-up depois da reunião. Minuta é feito para a resposta que você precisa antes da call seguir.",
      "Call difícil ao vivo é o problema. Minuta escuta localmente e rascunha uma resposta no seu tom enquanto o cliente ainda espera.",
      "Bot pode ser o formato errado. Fireflies é transparente sobre o Fred entrar como participante; isso funciona para muitos times. Em vendas reguladas ou sensíveis, Minuta fica fora da lista de participantes.",
      'O "private storage" do Fireflies Enterprise ainda é nuvem. No Minuta, a captura fica no desktop e o processamento cloud é desenhado para ser transitório e sem bot aparecendo na call.',
      "Comprador brasileiro e da LatAm. Fireflies transcreve PT e ES, mas tem site só em inglês. A gente ranqueia nativamente em PT-BR e ES.",
    ],
  },
  also: { heading: "Comparando outras ferramentas?", links: [] },
  cta: {
    heading: "Teste o Minuta na sua próxima customer ou discovery call",
    body: "7 dias com tudo desbloqueado. Use quando a conversa ficar difícil e você precisar da próxima resposta ao vivo.",
    primary: "Começar trial grátis",
    secondary: "Baixar pro macOS",
  },
};

const es: ComparePageCopy = {
  seo: {
    title: "Fireflies.ai vs. Minuta — notas después vs. respuestas en vivo",
    description:
      "Comparación honesta entre Fireflies y Minuta. Elige Fireflies para notetaker de equipo después de la call; elige Minuta para responder en vivo en una call difícil en inglés, portugués o español.",
    keywords:
      "alternativa fireflies, fireflies vs minuta, transcripción de reunión sin bot, alternativa recorderbot, fireflies español, copiloto de reunión sin bot",
  },
  vendor: VENDOR,
  eyebrow: "Comparación",
  h1: "Fireflies.ai graba la call. Minuta te ayuda a responder en ella.",
  intro:
    "Fireflies es un notetaker fuerte para grabaciones, transcripciones, resúmenes y flujos de CRM después de la reunión. Minuta es para el momento dentro de una call difícil con cliente o discovery, cuando necesitas la próxima respuesta ahora.",
  honesty: {
    heading: "Elige el correcto",
    pickThem: {
      name: "Fireflies.ai",
      bullets: [
        "Quieres grabaciones, transcripciones y resúmenes disponibles para el equipo después de la call.",
        "Quieres el stack más profundo de CRM — HubSpot, Salesforce, Affinity, Wealthbox, Greenhouse ya listos.",
        "Un bot en la call no es problema para tus prospects ni clientes, y un notetaker visible ayuda a tu flujo.",
        "Compras enterprise-led y SOC 2 + HIPAA-BAA en Enterprise cubre a tu comprador.",
        "Tu equipo trabaja en 5+ plataformas de vídeo y quieres un notetaker para todas.",
      ],
    },
    pickMinuta: [
      "Estás en una call difícil de ventas, customer o discovery en inglés, portugués o español y necesitas ayuda para responder mientras la call sigue.",
      "La política del cliente bloquea bots de terceros — ventas hacia legal, salud, finanzas, sector público.",
      "Quieres captura local de audio y procesamiento cloud con retención cero, sin un bot de reunión grabando como participante adicional.",
      "Hablas español o portugués y quieres la web, no solo el motor de transcripción, en tu idioma.",
      "Quieres copiloto sugiriendo respuesta en tiempo real, no solo resumen post-call.",
    ],
  },
  table: {
    heading: "Lado a lado",
    headers: { feature: "Función", them: "Fireflies", minuta: "Minuta" },
    rows: [
      {
        feature: "Bot en la reunión",
        them: {
          value: 'Sí — RecorderBot ("Fred") entra como participante',
          cite: { url: "https://fireflies.ai/pricing", capturedAt: "2026-04-30" },
        },
        minuta: { value: "No — sin bot, sin participante" },
      },
      {
        feature: "Procesamiento del audio",
        them: {
          value: "Nube (regiones EE.UU./UE)",
          cite: { url: "https://fireflies.ai/security", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Captura local, API transitoria segura" },
      },
      {
        feature: "Web nativa en PT",
        them: {
          value: "No — web solo en inglés (transcribe 60+ idiomas)",
          cite: {
            url: "https://fireflies.ai/blog/fireflies-portuguese-transcription",
            capturedAt: "2026-04-30",
          },
        },
        minuta: { value: "Sí — web completa en PT-BR" },
      },
      {
        feature: "Web nativa en ES",
        them: { value: "No — web solo en inglés" },
        minuta: { value: "Sí — web completa en ES" },
      },
      {
        feature: "Copiloto de respuesta en vivo",
        them: { value: "No — foco en resúmenes post-call" },
        minuta: { value: "Sí (Pro)" },
      },
      {
        feature: "Funciona offline", them: { value: "No" }, minuta: { value: "No (requiere internet)" },
      },
      {
        feature: "Integraciones de CRM",
        them: {
          value: "~42 — HubSpot, Salesforce, Affinity, Wealthbox, Greenhouse",
          cite: { url: "https://fireflies.ai/integrations", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Stripe + Cognito (CRM por exportación)" },
      },
      {
        feature: "Compliance",
        them: {
          value: "SOC 2 II, GDPR, HIPAA-BAA (Enterprise)",
          cite: { url: "https://fireflies.ai/security", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Privacidad por diseño (DPA en roadmap)" },
      },
      {
        feature: "Plataforma",
        them: {
          value: "Mac, Windows, móvil, Chrome",
          cite: { url: "https://fireflies.ai/pricing", capturedAt: "2026-04-30" },
        },
        minuta: { value: "macOS (Windows pronto)" },
      },
      {
        feature: "Plan gratis",
        them: {
          value: "Free US$ 0 — 800 min/mes",
          cite: { url: "https://fireflies.ai/pricing", capturedAt: "2026-04-30" },
        },
        minuta: { value: "7 días con todo · luego 1h/mes en Gratis" },
      },
      {
        feature: "De pago (por usuario, anual)",
        them: {
          value: "Desde US$ 10/u/mes Pro",
          cite: { url: "https://fireflies.ai/pricing", capturedAt: "2026-04-30" },
        },
        minuta: { value: "€9/mes" },
      },
    ],
    footnote: "Precios referencia 2026-04-30; confirma con cada empresa los valores actuales.",
  },
  whenMinuta: {
    heading: "Dónde gana Minuta",
    bullets: [
      "El momento en vivo importa más que el archivo. Fireflies está hecho para grabaciones, resúmenes y follow-up después de la reunión. Minuta está hecho para la respuesta que necesitas antes de que la call siga.",
      "La call difícil en vivo es el problema. Minuta captura audio localmente y redacta una respuesta en tu tono mientras el cliente sigue esperando.",
      "Un bot puede ser el formato equivocado. Fireflies es transparente sobre Fred entrando como participante; eso funciona para muchos equipos. En ventas reguladas o sensibles, Minuta queda fuera de la lista de participantes.",
      'La postura de privacidad es distinta. El "private storage" de Fireflies Enterprise sigue siendo nube de notetaker. Minuta evita el bot de reunión y usa rutas cloud revisadas con retención cero para transcripción y respuestas.',
      "Comprador en LatAm y España. Fireflies transcribe PT y ES pero tiene web solo en inglés. Nosotros rankeamos nativamente en ES y PT-BR.",
    ],
  },
  also: { heading: "¿Comparando otras herramientas?", links: [] },
  cta: {
    heading: "Prueba Minuta en tu próxima customer o discovery call",
    body: "7 días con todo desbloqueado. Úsalo cuando la conversación se ponga difícil y necesites la próxima respuesta en vivo.",
    primary: "Empezar prueba gratis",
    secondary: "Descargar para macOS",
  },
};

export const compareFirefliesCopy: ComparePageCopyByLang = { en, pt, es };
